/**
 * Adapter between the on-disk wire shape (GET /api/forecasts rowToWire + the
 * messages table) and the normalized run model. Branches are peer rows on the wire
 * (each carrying parentId/fork), so roots and branches hydrate uniformly via rowToRun;
 * there is no nested-branch round-trip.
 */
import { makeRun, makeEntry, STATUS } from "./model.js";
import { stampOccurrenceKeys } from "./eventIdentity.js";
import { normalizePct } from "../stageLabel.js";
import { ladderForTag, progressStepIndex, formatProgressStep } from "./progressSteps.js";
import { laneTier } from "./engines.js";

// Decode a persisted last_message. The progress tap writes it as
// JSON.stringify(data.data || null) (server/proxy/forecast.js), so a parsed string is
// the message and any other parsed value decodes to null. In practice data.data is a
// dict (e.g. {substeps: N}); without this, a reload of an in-flight forecast would render
// the raw JSON as the placeholder. Legacy plain strings fail JSON.parse and pass through.
function decodeLastMessage(raw) {
  if (raw == null) return null;
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") return parsed;
    return null;
  } catch {
    return raw;
  }
}

// last_* columns are written per-frame by the SSE proxy tap (server/proxy/forecast.js).
// The helper accepts snake_case (raw DB values) and camelCase (rowToWire) forms.
function progressFromRow(row) {
  const last_stage = row.last_stage ?? row.lastStage ?? null;
  const last_step = row.last_step ?? row.lastStep ?? null;
  const last_message = row.last_message ?? row.lastMessage ?? null;
  const last_progress = row.last_progress ?? row.lastProgress ?? null;
  // Decode the JSON-stringified message FIRST so the all-absent guard sees the
  // decoded value: a no-message frame persists JSON.stringify(null) (the literal
  // "null"), which decodes to null — so a row carrying only that should hydrate
  // no progress at all, not a {message: null} stub.
  const message = decodeLastMessage(last_message);
  if (last_stage == null && last_step == null && message == null && last_progress == null)
    return null;
  // Recompute the "Step N/X" line from the engine's ladder (quick vs deep) so a reloaded
  // in-flight run renders the same format as live. No round counter is persisted, so the
  // line is the bare label.
  const engine = row.serverContext?.metadata?.orchestrator ?? null;
  const ladder = ladderForTag(laneTier(engine));
  const stepIdx = progressStepIndex(ladder, last_stage, last_step, normalizePct(last_progress));
  return {
    stage: last_stage ?? null,
    step: last_step ?? null,
    stepIdx,
    message: stepIdx >= 0 ? formatProgressStep(ladder, stepIdx, null) : message,
    pct: normalizePct(last_progress),
  };
}

/**
 * A wire row's run identity: `id`, falling back to `forecastId`. rowToRun keys runs by it.
 * @param {{ id?: string | null, forecastId?: string | null }} row
 * @returns {string | null | undefined}
 */
export function rowRunId(row) {
  return row.id || row.forecastId;
}

/** @typedef {import("../../types/forecastWire.js").ForecastWire} ForecastWire */
/** @typedef {import("../../types/forecastWire.js").SharedForecastWire} SharedForecastWire */

/**
 * One wire row (owner leg or shared leg — the generated B1 types) to a run. Reads named fields
 * with explicit defaults; the shared leg's extra fields (`isOwn`, `owner`, `canEdit`) default
 * the owner way when absent.
 * @param {ForecastWire | SharedForecastWire} row
 */
export function rowToRun(row) {
  const rid = rowRunId(row);
  const fork = row.fork ?? {};
  const run = makeRun({
    runId: rid,
    // Provisional; hydrateRows replaces this with the resolved root runId.
    conversationId: row.parentId ?? rid,
    parentRunId: row.parentId ?? null,
    question: row.question || "",
    backend:
      (row.serverContext &&
        row.serverContext.metadata &&
        row.serverContext.metadata.orchestrator) ||
      null,
    status: row.status || STATUS.COMPLETED,
    createdAt: row.createdAt,
    forkDate: fork.forkDate ?? null,
    modification: fork.modification ?? null,
    // `forkSense` is what the branch ASSERTED (persisted at branch-create); `premise` is the
    // grading state for that assertion. Both are null on roots and on legacy branch rows.
    forkSense: fork.sense ?? null,
    premise: fork.premise ?? null,
    // CLEAR BRANCH's archive stamp (server/handlers/archiveBranch.js). Beside the premise,
    // not inside it: the premise sweep replaces `fork.premise` wholesale.
    clearedAt: fork.clearedAt ?? null,
    forkDerived: fork.derived ?? null,
  });
  run.forecastId = row.forecastId ?? null;
  run.traceId = row.traceId ?? null;
  // The current run's start (server `started_at`, restamped by a reforecast). Only set
  // when the wire carries it, so rows from older servers keep the createdAt clock.
  if (row.startedAt) run.startedAt = row.startedAt;
  run.contractKey = row.contractKey ?? null;
  run.contract = row.contract ?? null;
  // The preprocessed question, present even on a lean row (which has no contract), so
  // the manager card is named by it from first paint — see forecastDisplayName.
  run.contractQuestion = row.contractQuestion ?? row.contract?.question ?? null;
  // The lean list's card keys of contract / history, so list cards draw before the backfill.
  run.listSummary = row.listSummary ?? null;
  // This is the only ingest path that bypasses filterAndStampEvents, so the stable
  // occurrence key must be stamped here. Legacy and fresh rows describing the same
  // occurrence derive the same key from title+date, which preserves collapse/promote
  // state and slide anchors.
  run.events = row.events ? stampOccurrenceKeys(row.events) : (row.events ?? null);
  run.serverContext = row.serverContext ?? null;
  run.schedule = row.schedule ?? null;
  run.contractStatus = row.contractStatus ?? null;
  // The VIEWER's alert prefs: forecasts.notify on an owned row, the viewer's own
  // forecast_notify on a shared one (never the owner's).
  run.notify =
    row.notify && typeof row.notify === "object" && !Array.isArray(row.notify) ? row.notify : {};
  // Trigger-mint lineage (rowToWire.triggerOrigin) — marks machine-minted rows.
  run.triggerOrigin = row.triggerOrigin ?? null;
  run.series = row._series ?? {};
  // Branch signal-projection impacts (dotted TSD lines) — persisted in
  // server_ctx.seriesImpacts, surfaced by rowToWire as row.seriesImpacts. Restore
  // it so a branch's projection redraws on reload (set live by seriesImpactsSet).
  run.seriesImpacts = row.seriesImpacts ?? {};
  run.resolution = row.resolution ?? null;
  run.serverPrediction = row.serverPrediction ?? null;
  run.serverExplanation = row.serverExplanation ?? null;
  run.report = row.report ?? null;
  run.history = row.history ?? [];
  run.move = row.move ?? null;
  run.progress = progressFromRow(row);
  run.forkEvent = fork.forkEvent ?? null;
  // A derived branch's own trigger generation (server/handlers/derivedBranchTriggers.js):
  // "ready"/"failed" are terminal, so the client does not ask again.
  run.derivedTriggersStatus = fork.branchTriggers?.status ?? null;
  // Append-only report-pointer history (rowToWire → reportPointers). The read
  // path selects the latest pointer and fetches its report via the read proxies.
  run.reportPointers = row.reportPointers ?? [];
  // Owner-leg rows (GET /api/forecasts) never carry
  // `isOwn`/`owner` at all — defaulting `isOwn` to `true` here is what keeps
  // every existing row's behavior unchanged; only a row hydrated from
  // GET /api/forecasts/shared ever supplies `isOwn: false` + a real `owner`.
  run.isOwn = row.isOwn ?? true;
  run.owner = row.owner ?? null;
  // Whether THIS viewer may change the shared reforecast cadence. Reported by
  // the server (`listSharedForecasts`' canEdit, from the same
  // `canSetSharedCadence` the write route enforces), never re-derived here — the
  // browser does not know the owner's organization, and a client that guessed
  // would show a slider that 403s. Defaults TRUE for the same reason `isOwn`
  // does: every owner-leg row omits the field, and an owner may always set their
  // own cadence. Only the shared lane ever sends `false`.
  run.canEdit = row.canEdit ?? true;
  // Whether this forecast has any share (rowToWire → isShared), so the SHARE
  // control can read SHARING without opening the dialog. Defaults FALSE, the
  // opposite way from `isOwn` above and for the same reason: absence must mean
  // the safe, quiet answer. A row from a lean query, an older cached response,
  // or the shared lane (which strips the field) all read "not shared" rather
  // than claiming a share that may not exist.
  run.isShared = row.isShared === true;
  // Someone other than the viewer watchlists this — the eye must not drive the
  // cadence. Owner leg only (the shared wire drops it), and FALSE by default, so
  // a row that predates the field behaves exactly as it always has.
  run.othersWatching = row.othersWatching === true;
  // When the viewer RECEIVED this row — the grant time on the shared leg. The
  // Forecast Manager merges owned + shared on it (mergeByReceivedAt); dropped here,
  // a shared row sorts by its original creation date instead of arriving on top.
  run.receivedAt = row.receivedAt ?? null;
  return run;
}

/**
 * The DB `role` column is CHECK-constrained to 'user'|'assistant', so the richer entry role
 * (and kind/runId) ride in `metadata`. ts prefers metadata.ts (the original client
 * timestamp) and falls back to created_at for legacy rows.
 */
export function messageToEntry(msg) {
  const md = { ...(msg.metadata || {}) };
  const role = md.entryRole ?? msg.role;
  const kind = md.kind ?? null;
  const runId = md.runId ?? null;
  const ts = md.ts ?? msg.created_at;
  delete md.entryRole;
  delete md.kind;
  delete md.runId;
  delete md.ts;
  return makeEntry({ role, kind, runId, content: msg.content, ts, metadata: md });
}

/**
 * Models a stored `messages` row, including `created_at`. Do NOT POST it, because the
 * server assigns `created_at`; use `entryToMessageBody` for the write payload.
 */
export function entryToMessageRow(entry) {
  const dbRole = entry.role === "user" || entry.role === "assistant" ? entry.role : "assistant";
  // The reserved keys belong to the entry's explicit fields, never the bag. Strip any that
  // leaked in, then re-add them from the fields so a stale bag value cannot override them on
  // hydrate and plain user/assistant rows do not accrue redundant metadata.
  const metadata = { ...(entry.metadata || {}) };
  delete metadata.entryRole;
  delete metadata.kind;
  delete metadata.runId;
  delete metadata.ts;
  if (entry.role !== "user" && entry.role !== "assistant") metadata.entryRole = entry.role;
  if (entry.kind != null) metadata.kind = entry.kind;
  if (entry.runId != null) metadata.runId = entry.runId;
  // Persist the original client ts so messageToEntry can recover it after the
  // server assigns a later created_at.
  metadata.ts = entry.ts;
  return { role: dbRole, content: entry.content, metadata, created_at: entry.ts };
}

/**
 * POST payload for `/api/forecasts/:id/messages`. The DB assigns `created_at` server-side
 * (`server/handlers/messages.js`), so `ts` is READ-ONLY on writes: send the body, then read
 * `ts` back from the server-returned row.
 */
export function entryToMessageBody(entry) {
  const { role, content, metadata } = entryToMessageRow(entry);
  return { role, content, metadata };
}

/**
 * Only roots get an entry in conversationsById, keyed by root runId and sourced from
 * messagesByForecastId[root.forecastId]. Branches share the root's conversation and receive
 * no separate entry.
 */
export function hydrateRows(wireRows, messagesByForecastId = {}, { lean = false } = {}) {
  const runsById = {};
  for (const row of wireRows) {
    try {
      const run = rowToRun(row);
      // Lean rows carry no contract/events/serverExplanation/full serverContext.
      // Mark the run so the save effect won't POST it back (which would upsert
      // nulls over those columns) before the detail backfill fills them in.
      run.detailPending = lean;
      runsById[run.runId] = run;
    } catch (e) {
      console.warn(
        "hydrateRows: skipping unhydratable row",
        { id: row?.id, forecastId: row?.forecastId },
        e,
      );
    }
  }
  // The seen set guards against cycles so a corrupt row chain cannot loop forever.
  const rootOf = (id, seen = new Set()) => {
    const r = runsById[id];
    if (!r || r.parentRunId == null || seen.has(id)) return id;
    // Broken chain: the parent isn't present (partial list response or corrupted
    // FK). Treat THIS run as the top of the present subtree rather than walking to
    // the absent id — otherwise conversationId points at a row that never gets a
    // conversationsById entry (only rootId === runId rows do), orphaning the thread.
    if (!runsById[r.parentRunId]) return id;
    seen.add(id);
    return rootOf(r.parentRunId, seen);
  };
  const conversationsById = {};
  for (const run of Object.values(runsById)) {
    const rootId = rootOf(run.runId);
    run.conversationId = rootId;
    if (rootId === run.runId) {
      const msgs = (run.forecastId && messagesByForecastId[run.forecastId]) || [];
      conversationsById[rootId] = { entries: msgs.map(messageToEntry) };
    }
  }
  return { runsById, conversationsById };
}
